import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { WarningSolidIcon } from '@/components/ui/warning-solid-icon';
import { useLotSizingPurchaseOrderDraft } from '@/features/lot-sizing/lot-sizing-purchase-order-draft-context';
import { orderedQuantitySchema } from '@/features/lot-sizing/schema/ordered-quantity-schema';
import { cn } from '@/lib/utils';

type LotSizingQuantityCellProps = {
  value: number;
  rowId: string;
  quantityPerPackage: number | null;
  minimumOrderQuantity: number;
  currentQuantityPerPackage?: number | null;
  onValueChange?: (rowId: string, quantity: number) => void;
  disabled?: boolean;
  className?: string;
};

export function LotSizingQuantityCell({
  value,
  rowId,
  quantityPerPackage,
  minimumOrderQuantity,
  currentQuantityPerPackage,
  onValueChange,
  disabled,
  className,
}: LotSizingQuantityCellProps) {
  const { t } = useTranslation('lotSizing');
  const errorId = useId();
  const [rawValue, setRawValue] = useState(String(value));
  const [error, setError] = useState<string | null>(null);
  const invalidInputRef = useRef(false);
  const { setQuantityError } = useLotSizingPurchaseOrderDraft();

  useEffect(() => {
    if (invalidInputRef.current) {
      return;
    }
    setRawValue(String(value));
    setError(null);
    setQuantityError(rowId, false);
  }, [rowId, setQuantityError, value]);

  useEffect(
    () => () => setQuantityError(rowId, false),
    [rowId, setQuantityError],
  );

  const quantity = Number(rawValue);
  const qtyCtn = Math.max(1, currentQuantityPerPackage ?? 1);
  const isBelowMOQ = quantity < minimumOrderQuantity;
  const isNotCartonMultiple =
    Number.isInteger(quantity) &&
    quantity > 0 &&
    qtyCtn > 1 &&
    quantity % qtyCtn !== 0;
  const hasWarning = error === null && (isBelowMOQ || isNotCartonMultiple);

  return (
    <div>
      <div className='relative w-fit'>
        <Input
          aria-describedby={error ? errorId : undefined}
          aria-invalid={error !== null}
          aria-label={t('quantity')}
          className={cn(
            'h-8 w-20',
            hasWarning &&
              'border-amber-500 text-amber-600 focus-visible:ring-amber-500',
            className,
          )}
          disabled={disabled}
          min={1}
          onChange={(event) => {
            setRawValue(event.target.value);
            const result = orderedQuantitySchema(quantityPerPackage).safeParse(
              Number(event.target.value),
            );
            const message = result.success
              ? null
              : result.error.issues[0].message;
            setError(message);
            invalidInputRef.current = !result.success;
            setQuantityError(rowId, !result.success);
            if (result.success) {
              onValueChange?.(rowId, result.data);
            }
          }}
          step={quantityPerPackage ?? 1}
          type='number'
          value={rawValue}
        />
        {hasWarning && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                aria-label={t('quantityWarning')}
                className='pointer-events-auto absolute top-1/2 right-7 inline-flex -translate-y-1/2 text-amber-600'
                type='button'
              >
                <WarningSolidIcon aria-hidden='true' className='size-4' />
              </button>
            </TooltipTrigger>
            <TooltipContent className='max-w-64'>
              {isBelowMOQ && <div>{t('quantityBelowMOQTooltip')}</div>}
              {isNotCartonMultiple && (
                <div>
                  {t('quantityNotCartonMultipleTooltip', {
                    qtyCtn,
                  })}
                </div>
              )}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
      {error && (
        <p
          className='max-w-48 whitespace-normal text-destructive text-xs'
          id={errorId}
          role='alert'
        >
          {t(error, { qtyCtn: quantityPerPackage })}
        </p>
      )}
    </div>
  );
}
